import React, { Component, RefObject, SyntheticEvent, createRef } from 'react';
import { Grid } from '@material-ui/core';
import {
    NOT_NUMBER_REGEXP,
    SofaFormControl,
    SofaFormControlDescriptionWithGtm,
    SofaFormWithGtm,
    SofaInput,
    getPropsForSofaInput,
    maxLengthFilter,
    minLengthValidator,
    setFocusToNextInputField,
    stringRequiredValidator,
    textFilter
} from '@sofology/sofologycomponents';

import './SofaSortCodeInput.scss';

import { analyticsHelper, errorToBasketErrorModel, messageArrayToHelperText } from 'helpers';
import { SORTCODE_MIN_LENGTH_ERROR_MESSAGE, SORTCODE_REQUIRED_ERROR_MESSAGE, SORT_CODE_LENGTH } from './sofaSortCodeInputData';

const GTM_FORM_NAME = 'finance';

interface OwnProps {
    label: string;
    parentControl: any;
    name: string;
    onChange: (e: any) => void;
    onBlur: (e: SyntheticEvent) => void;
    value: any;
    helperText: string | null;
    addErrorClass: boolean;
    ref: RefObject<any> | undefined;
    caretPosition: number | undefined;
    helperTextType?: 'error' | 'info';
}

interface OwnState {
    sortCodeForm: SofaFormWithGtm<SortCodeControls>;
    originalParentControlValidateFunc: () => Promise<SofaFormControl<any>>;
}

interface SortCodeControls {
    sortCode1: SofaFormControl<string>;
    sortCode2: SofaFormControl<string>;
    sortCode3: SofaFormControl<string>;
}

export class SofaSortCodeInput extends Component<OwnProps, OwnState> {
    private element = React.createRef<HTMLDivElement>();
    private inputElement = React.createRef<HTMLInputElement>();

    constructor(props: OwnProps) {
        super(props);
        let sortCode1 = '';
        let sortCode2 = '';
        let sortCode3 = '';

        if (props.value !== '' && props.value !== null) {
            const splitedCodes: string[] = props.value.split('-');

            sortCode1 = splitedCodes[0];
            sortCode2 = splitedCodes[1];
            sortCode3 = splitedCodes[2];
        }

        this.state = {
            sortCodeForm: new SofaFormWithGtm<SortCodeControls>(
                {
                    sortCode1: {
                        name: 'sortCode1',
                        id: 'sortCode1',
                        defaultValue: sortCode1,
                        filters: [
                            textFilter(NOT_NUMBER_REGEXP),
                            maxLengthFilter(SORT_CODE_LENGTH)
                        ],
                        validators: [
                            stringRequiredValidator(SORTCODE_REQUIRED_ERROR_MESSAGE),
                            minLengthValidator(SORTCODE_MIN_LENGTH_ERROR_MESSAGE, SORT_CODE_LENGTH)
                        ],
                        ref: createRef<SofaInput>(),
                        gtmFieldName: 'sortcode1',
                        onMaxLength: this.setFocusToNextField
                    } as SofaFormControlDescriptionWithGtm<string>,
                    sortCode2: {
                        name: 'sortCode2',
                        id: 'sortCode2',
                        defaultValue: sortCode2,
                        filters: [
                            textFilter(NOT_NUMBER_REGEXP),
                            maxLengthFilter(SORT_CODE_LENGTH)
                        ],
                        validators: [
                            stringRequiredValidator(SORTCODE_REQUIRED_ERROR_MESSAGE),
                            minLengthValidator(SORTCODE_MIN_LENGTH_ERROR_MESSAGE, SORT_CODE_LENGTH)
                        ],
                        ref: createRef<SofaInput>(),
                        gtmFieldName: 'sortcode2',
                        onMaxLength: this.setFocusToNextField
                    } as SofaFormControlDescriptionWithGtm<string>,
                    sortCode3: {
                        name: 'sortCode3',
                        id: 'sortCode3',
                        defaultValue: sortCode3,
                        filters: [
                            textFilter(NOT_NUMBER_REGEXP),
                            maxLengthFilter(SORT_CODE_LENGTH)
                        ],
                        validators: [
                            stringRequiredValidator(SORTCODE_REQUIRED_ERROR_MESSAGE),
                            minLengthValidator(SORTCODE_MIN_LENGTH_ERROR_MESSAGE, SORT_CODE_LENGTH)
                        ],
                        ref: createRef<SofaInput>(),
                        gtmFieldName: 'sortcode3'
                    } as SofaFormControlDescriptionWithGtm<string>
                },
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'sortCodeForm',
                () => this.state,
                GTM_FORM_NAME,
                analyticsHelper.sendToGoogleMessageAboutInputChangeCompleted.bind(analyticsHelper),
                errorToBasketErrorModel
            ),
            originalParentControlValidateFunc: this.props.parentControl.validate
        };

        this.mountParentControlValidateFunc();
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>) {
        if ((prevState.sortCodeForm.controls.sortCode1.value !== this.state.sortCodeForm.controls.sortCode1.value)
            || (prevState.sortCodeForm.controls.sortCode2.value !== this.state.sortCodeForm.controls.sortCode2.value)
            || (prevState.sortCodeForm.controls.sortCode3.value !== this.state.sortCodeForm.controls.sortCode3.value)) {
            this.props.parentControl.onChange({ target: { value: this.getFormData() } });
        }

        if (prevState.sortCodeForm.controls.sortCode3.isBlurHandled !== this.state.sortCodeForm.controls.sortCode3.isBlurHandled) {
            this.props.parentControl.onBlur();
        }
    }

    componentWillUnmount(): void {
        this.unmountParentControlValidateFunc();
    }

    render(): JSX.Element {
        const { sortCode1, sortCode2, sortCode3 } = this.state.sortCodeForm.controls;
        const errorMessages = messageArrayToHelperText([sortCode1.errorMessage, sortCode2.errorMessage, sortCode3.errorMessage, this.props.parentControl.errorMessage]);
        const errorClass = Boolean(sortCode1.errorMessage || sortCode2.errorMessage || sortCode3.errorMessage || this.props.parentControl.errorMessage);

        return (
            <div className='sofa-sort-code-form' ref={this.element}>
                <div className='validation-scroll-container'>
                    <Grid container spacing={3}>
                        <Grid item xs={12} className='validation-scroll-container'>
                            <div className='sofa-sort-code-form__input-title'>Account sort code *</div>
                            <div className='sofa-sort-code-form__sortcode'>
                                <Grid container>
                                    <Grid item xs={4} sm={2}>
                                        <SofaInput
                                            label='00'
                                            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', autoComplete: 'cc-csc', maxLength: '2' }}
                                            {...getPropsForSofaInput(sortCode1)}
                                            helperText={errorMessages}
                                            addErrorClass={errorClass}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={2}>
                                        <SofaInput
                                            label='00'
                                            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', autoComplete: 'cc-csc', maxLength: '2' }}
                                            {...getPropsForSofaInput(sortCode2)}
                                            helperText={null}
                                            addErrorClass={errorClass}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={2}>
                                        <SofaInput
                                            label='00'
                                            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', autoComplete: 'cc-csc', maxLength: '2' }}
                                            {...getPropsForSofaInput(sortCode3)}
                                            helperText={null}
                                            addErrorClass={errorClass}
                                        />
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </div>
        );
    }

    public setFocus(): void {
        this.inputElement.current?.focus();
        this.inputElement.current?.setSelectionRange(0, 0);
    }

    setFocusToNextField = (control: SofaFormControl<string>) => {
        setFocusToNextInputField(control, this.state.sortCodeForm.controls);
    };

    private mountParentControlValidateFunc() {
        const originalParentControlValidateFunc = this.props.parentControl.validate;
        this.props.parentControl.validate = () => {
            return this.state.sortCodeForm.validate().then(invalidControls => {
                if (invalidControls.length === 0) {
                    return originalParentControlValidateFunc();
                }

                return this.props.parentControl;
            });
        };
    }

    private unmountParentControlValidateFunc() {
        this.props.parentControl.validate = this.state.originalParentControlValidateFunc;
    }

    getFormData(): string {
        const { sortCode1, sortCode2, sortCode3 } = this.state.sortCodeForm.controls;

        return `${sortCode1.value}-${sortCode2.value}-${sortCode3.value}`;
    }
}